/**
 * Tab Proprietari nell'Admin Panel.
 * CRUD completo per la gestione proprietari.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ownersApi, OwnerItem, OwnerCreate, OwnerUpdate } from '../../api/owners'
import { SkeletonList } from '../../components/Skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { UserPlus, Pencil, Trash2, Search } from 'lucide-react'


export function OwnersTab() {
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editingOwner, setEditingOwner] = useState<OwnerItem | null>(null)
  const [deletingOwner, setDeletingOwner] = useState<OwnerItem | null>(null)
  const [search, setSearch] = useState('')

  const { data: owners, isLoading } = useQuery<OwnerItem[]>({
    queryKey: ['owners'],
    queryFn: () => ownersApi.list(200),
  })

  const createMutation = useMutation({
    mutationFn: (data: OwnerCreate) => ownersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owners'] })
      setShowCreate(false)
      setCreateForm({ email: '', full_name: '', phone: '', notes: '' })
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante la creazione')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ ownerId, data }: { ownerId: string; data: OwnerUpdate }) =>
      ownersApi.update(ownerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owners'] })
      setEditingOwner(null)
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante l\'aggiornamento')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (ownerId: string) => ownersApi.delete(ownerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['owners'] })
      setDeletingOwner(null)
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante l\'eliminazione')
    },
  })

  const [createForm, setCreateForm] = useState<OwnerCreate>({
    email: '', full_name: '', phone: '', notes: '',
  })

  const [editForm, setEditForm] = useState<OwnerUpdate>({})

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.email.trim()) {
      alert('Email obbligatoria')
      return
    }
    createMutation.mutate(createForm)
  }

  const handleEdit = (owner: OwnerItem) => {
    setEditingOwner(owner)
    setEditForm({ full_name: owner.full_name || '', phone: owner.phone || '' })
  }

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingOwner) return
    updateMutation.mutate({ ownerId: editingOwner.id, data: editForm })
  }

  const filteredOwners = owners?.filter(o =>
    !search ||
    o.email.toLowerCase().includes(search.toLowerCase()) ||
    (o.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (o.phone || '').includes(search)
  )

  if (isLoading) return <SkeletonList count={5} />

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Proprietari</h2>
        <Button onClick={() => setShowCreate(true)}>
          <UserPlus className="size-4 mr-2" />
          Aggiungi Proprietario
        </Button>
      </div>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per email, nome, telefono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Telefono</TableHead>
              <TableHead>Pazienti</TableHead>
              <TableHead>Prima vista</TableHead>
              <TableHead>Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredOwners?.map((owner) => (
              <TableRow key={owner.id}>
                <TableCell>
                  <span className="font-medium">
                    {owner.email}
                  </span>
                </TableCell>
                <TableCell>{owner.full_name || '-'}</TableCell>
                <TableCell>{owner.phone || '-'}</TableCell>
                <TableCell>
                  <Badge variant="outline">{owner.patients_count}</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(owner.first_seen_at).toLocaleDateString('it-IT')}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="size-8" onClick={() => handleEdit(owner)} title="Modifica">
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive hover:text-destructive"
                      onClick={() => setDeletingOwner(owner)}
                      title="Elimina"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Proprietario</DialogTitle>
            <DialogDescription>Crea un nuovo proprietario manualmente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} required />
            </div>
            <div>
              <Label>Nome completo</Label>
              <Input value={createForm.full_name} onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })} />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creazione...' : 'Crea Proprietario'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingOwner} onOpenChange={(open) => !open && setEditingOwner(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Proprietario</DialogTitle>
            <DialogDescription>{editingOwner?.email}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div>
              <Label>Nome completo</Label>
              <Input value={editForm.full_name || ''} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingOwner(null)}>Annulla</Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deletingOwner} onOpenChange={(open) => !open && setDeletingOwner(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina Proprietario</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare <strong>{deletingOwner?.email}</strong>?
              {deletingOwner && deletingOwner.patients_count > 0 && (
                <p className="text-destructive mt-2">
                  Attenzione: il proprietario ha {deletingOwner.patients_count} pazienti associati.
                  Devi prima eliminare o trasferire i pazienti.
                </p>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingOwner(null)}>Annulla</Button>
            <Button variant="destructive" onClick={() => deletingOwner && deleteMutation.mutate(deletingOwner.id)} disabled={deleteMutation.isPending || (deletingOwner?.patients_count || 0) > 0}>
              {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
